import { nanoIdSchema } from "../shared/id.mjs";
import { getTaskInputSchema, getTaskOutputSchema } from "../task/output.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/internal/task.ts
/**
* Mirror of `nsfwSafetyScoreQuerySchema` from the server's
* `src/service/helper/moderation/type.ts`. `NsfwSafetyScore` values are
* the small finite set `[1, 2, 3, 4, 5, 7, 9]`. Kept inline so the
* contract package stays free of server-only moderation imports;
* canonical definition stays on the server alongside the moderation
* service code.
*
* Coerces from string first because HTTP query-string params arrive as
* strings — a plain literal union would reject them. Used only at the
* contract boundary; internal service-layer call sites use the
* server-side `nsfwSafetyScoreSchema` (already typed as `NsfwSafetyScore`).
*/
const nsfwSafetyScoreQuerySchema = z.coerce.number().pipe(z.literal([
	1,
	2,
	3,
	4,
	5,
	7,
	9
]));
const imageSafetyScoreSchema = z.discriminatedUnion("result", [z.object({ result: z.literal("no_image") }), z.object({
	result: z.literal("success"),
	mediaSafetyScore: z.number().int(),
	mediaTextSafetyScore: z.number().int().nullable()
})]);
const taskModerationResultSchema = z.discriminatedUnion("result", [z.object({ result: z.literal("not_ready") }), z.object({
	result: z.literal("ready"),
	imageSafetyScore: imageSafetyScoreSchema,
	promptSafetyScore: z.number().int().optional()
})]);
const internalTaskContract = oc.prefix("/internal/task").tag("InternalTask").router({
	getById: oc.route({
		summary: "Get a task by ID (bypasses ownership check)",
		description: "Returns the current status and outputs of any task. Internal callers are trusted and skip user ownership verification. When `min_safety_score` is provided, `outputs` is blanked to an empty object ONLY when the task image has been moderated and its persisted safety score is below the threshold (known-unsafe). Tasks whose safety score is missing or still pending are returned as-is — callers that need a meaningful gate must opt their task into moderation at creation time (`moderationPolicy: \"force\"` on `/internal/task/image/create`).",
		method: "GET",
		path: "/{id}"
	}).input(getTaskInputSchema.extend({ min_safety_score: nsfwSafetyScoreQuerySchema.optional() })).output(getTaskOutputSchema).errors({ NOT_FOUND: {
		status: 404,
		message: "Task not found"
	} }),
	getModeration: oc.route({
		summary: "Get the moderation result of a task",
		description: "Returns safety scores for the task prompt and generated image. Returns `{ result: \"not_ready\" }` with HTTP 200 when the image moderation has not completed yet — the caller should keep polling. Returns 404 when the task does not exist.",
		method: "GET",
		path: "/{id}/moderation"
	}).input(z.object({ id: nanoIdSchema })).output(taskModerationResultSchema).errors({ NOT_FOUND: {
		status: 404,
		message: "Task not found"
	} })
});
//#endregion
export { internalTaskContract, taskModerationResultSchema };
